import React, { useState } from 'react';
import { 
  FiDownload, 
  FiUpload, 
  FiX, 
  FiFileText, 
  FiCheckCircle, 
  FiAlertCircle,
  FiFile,
  FiLoader,
  FiInfo,
  FiDatabase,
  FiCode,
  FiImage,
  FiPackage,
  FiFolder,
  FiTool,
  FiUsers
} from 'react-icons/fi';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

const ExportImportModal = ({ isOpen, onClose, type = 'all' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [activeTab, setActiveTab] = useState('export');
  const [exportFormat, setExportFormat] = useState('excel');
  const [exportDataType, setExportDataType] = useState(type);

  const getTypeLabel = () => {
    const labels = {
      all: 'All Data',
      products: 'Products',
      categories: 'Categories',
      repairs: 'Repair Articles',
      users: 'Users'
    };
    return labels[exportDataType] || labels[type] || 'Data';
  };

  const getTypeIcon = () => {
    const icons = {
      all: '💾',
      products: '📱',
      categories: '📁',
      repairs: '🔧',
      users: '👥'
    };
    return icons[exportDataType] || icons[type] || '📄';
  };

  // Full Backup Export (JSON)
  const handleFullBackup = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/backup/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `grandfathersmile_backup_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Full backup exported successfully!');
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Regular Export (Excel or JSON)
  const handleExport = async () => {
    setIsLoading(true);
    try {
      let endpoint = '';
      let filename = '';

      if (exportFormat === 'excel') {
        endpoint = `/export/excel/${exportDataType}`;
        filename = `${exportDataType}_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      } else if (exportFormat === 'json') {
        endpoint = `/export/json/${exportDataType}`;
        filename = `${exportDataType}_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      }

      const response = await api.get(endpoint, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export completed! File saved as ${filename}`);
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Import Backup (JSON file from backup/export)
  const handleImportBackup = async () => {
    if (!importFile) {
      toast.error('Please select a backup file to import');
      return;
    }
    
    if (!importFile.name.endsWith('.json')) {
      toast.error('Only JSON backup files are allowed');
      return;
    }
    
    setIsLoading(true);
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await api.post('/backup/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setImportResult(response.data);
      
      const hasErrors = response.data.results && 
        Object.values(response.data.results).some(r => r.errors && r.errors.length > 0);
      
      if (!hasErrors) {
        toast.success('Backup restored successfully!');
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 2000);
      } else {
        toast.warning('Backup restored with errors. Check details below.');
      }
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const exportFormats = [
    { id: 'excel', name: 'Excel', icon: FiFile, color: 'green', description: 'Spreadsheet format (XLSX)' },
    { id: 'json', name: 'JSON', icon: FiCode, color: 'blue', description: 'Structured data format' }
  ];

  const dataTypes = [
    { id: 'all', name: 'All Data', icon: FiPackage, description: 'Export everything' },
    { id: 'products', name: 'Products', icon: FiPackage, description: 'All products' },
    { id: 'categories', name: 'Categories', icon: FiFolder, description: 'All categories' },
    { id: 'repairs', name: 'Repair Articles', icon: FiTool, description: 'All repair articles' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{getTypeIcon()}</span>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Import/Export {getTypeLabel()}</h2>
              <p className="text-sm text-gray-500 mt-1">Manage your data with backup support</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
            disabled={isLoading}
          >
            <FiX size={24} />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-gray-200 sticky top-[73px] bg-white z-10">
          <button
            onClick={() => setActiveTab('export')}
            className={`flex-1 py-3 text-center font-medium transition-colors ${
              activeTab === 'export'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiDownload className="inline mr-2" />
            Export Data
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`flex-1 py-3 text-center font-medium transition-colors ${
              activeTab === 'import'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiUpload className="inline mr-2" />
            Import Data
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Export Tab */}
          {activeTab === 'export' && (
            <div className="space-y-6">
              {/* Full Backup Section */}
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border-2 border-purple-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <FiDatabase className="text-purple-600" />
                      Full Backup (JSON)
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Export complete database backup as JSON file. Includes all products, categories, 
                      repairs, and users. Perfect for full system backup and migration.
                    </p>
                    <button
                      onClick={handleFullBackup}
                      disabled={isLoading}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                    >
                      <FiDatabase className="w-4 h-4" />
                      Export Full Backup (JSON)
                    </button>
                  </div>
                  <FiDatabase className="w-16 h-16 text-purple-400 opacity-50 hidden sm:block" />
                </div>
              </div>

              {/* Regular Export Section */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h3 className="text-md font-semibold text-gray-900 mb-3">Regular Export (Without Images)</h3>
                
                {/* Format Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Format</label>
                  <div className="grid grid-cols-2 gap-3">
                    {exportFormats.map((format) => (
                      <button
                        key={format.id}
                        onClick={() => setExportFormat(format.id)}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          exportFormat === format.id
                            ? `border-${format.color}-500 bg-${format.color}-50`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <format.icon className={`w-5 h-5 mx-auto mb-1 ${exportFormat === format.id ? `text-${format.color}-600` : 'text-gray-400'}`} />
                        <div className="text-sm font-medium">{format.name}</div>
                        <div className="text-xs text-gray-500">{format.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Data Type Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Data to Export</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {dataTypes.map((dt) => (
                      <button
                        key={dt.id}
                        onClick={() => setExportDataType(dt.id)}
                        className={`p-2 rounded-lg border transition-all text-center ${
                          exportDataType === dt.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <dt.icon className={`w-4 h-4 mx-auto mb-1 ${exportDataType === dt.id ? 'text-blue-600' : 'text-gray-400'}`} />
                        <div className="text-xs font-medium">{dt.name}</div>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Export Button */}
                <button
                  onClick={handleExport}
                  disabled={isLoading}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {isLoading ? <FiLoader className="animate-spin" /> : <FiDownload />}
                  Export {getTypeLabel()} as {exportFormats.find(f => f.id === exportFormat)?.name}
                </button>
              </div>
            </div>
          )}
          
          {/* Import Tab */}
          {activeTab === 'import' && (
            <div className="space-y-6">
              {/* Import Backup Section */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <FiDatabase className="text-green-600" />
                      Restore from Backup
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Upload a JSON backup file created from this system (Full Backup export). 
                      This will restore ALL data including products, categories, repairs, and users.
                      <br />
                      <span className="text-yellow-600 text-xs mt-1 block">
                        ⚠️ Warning: This will replace all existing data!
                      </span>
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Backup File (JSON)
                        </label>
                        <input
                          type="file"
                          accept=".json"
                          onChange={(e) => {
                            setImportFile(e.target.files[0]);
                            setImportResult(null);
                          }}
                          className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100 cursor-pointer"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Max file size: 50MB | Only JSON backup files from this system
                        </p>
                      </div>
                      
                      <button
                        onClick={handleImportBackup}
                        disabled={isLoading || !importFile}
                        className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <>
                            <FiLoader className="animate-spin" />
                            Restoring Backup...
                          </>
                        ) : (
                          <>
                            <FiUpload />
                            Restore Backup
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <FiDatabase className="w-16 h-16 text-green-400 opacity-50 hidden sm:block" />
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FiInfo className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-1">Import Information</p>
                    <ul className="text-xs text-blue-800 space-y-1">
                      <li>• Only JSON backup files created from this system can be imported</li>
                      <li>• Import will replace all existing data (products, categories, repairs, users)</li>
                      <li>• Images are referenced by filename - actual images must be restored separately</li>
                      <li>• To restore images, also copy the 'uploads' folder from your backup</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Import Results */}
              {importResult && (
                <div className={`rounded-lg p-4 ${
                  (importResult.errors?.length === 0 && 
                   (!importResult.results || Object.values(importResult.results).every(r => r.errors?.length === 0)))
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-yellow-50 border border-yellow-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {(importResult.errors?.length === 0 && 
                      (!importResult.results || Object.values(importResult.results).every(r => r.errors?.length === 0))) ? (
                      <FiCheckCircle className="text-green-600 mt-0.5 flex-shrink-0" size={20} />
                    ) : (
                      <FiAlertCircle className="text-yellow-600 mt-0.5 flex-shrink-0" size={20} />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 mb-2">
                        {importResult.message || 'Restore completed'}
                      </p>
                      
                      {/* Summary stats */}
                      {importResult.summary && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          {importResult.summary.categories !== undefined && (
                            <div className="bg-white rounded p-2 text-center">
                              <div className="text-lg font-bold text-green-600">{importResult.summary.categories}</div>
                              <div className="text-xs text-gray-500">Categories</div>
                            </div>
                          )}
                          {importResult.summary.products !== undefined && (
                            <div className="bg-white rounded p-2 text-center">
                              <div className="text-lg font-bold text-blue-600">{importResult.summary.products}</div>
                              <div className="text-xs text-gray-500">Products</div>
                            </div>
                          )}
                          {importResult.summary.repairs !== undefined && (
                            <div className="bg-white rounded p-2 text-center">
                              <div className="text-lg font-bold text-purple-600">{importResult.summary.repairs}</div>
                              <div className="text-xs text-gray-500">Repairs</div>
                            </div>
                          )}
                          {importResult.summary.users !== undefined && (
                            <div className="bg-white rounded p-2 text-center">
                              <div className="text-lg font-bold text-orange-600">{importResult.summary.users}</div>
                              <div className="text-xs text-gray-500">Users</div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Detailed results */}
                      {importResult.results && (
                        <div className="space-y-2 mt-3">
                          {Object.entries(importResult.results).map(([key, value]) => {
                            const data = value;
                            if (data.imported + data.updated > 0 || (data.errors && data.errors.length > 0)) {
                              return (
                                <div key={key} className="text-sm border-t border-gray-200 pt-2">
                                  <p className="font-medium capitalize mb-1">{key}:</p>
                                  <div className="flex gap-4 ml-2 text-xs">
                                    <span className="text-green-600">New: {data.imported || 0}</span>
                                    <span className="text-blue-600">Updated: {data.updated || 0}</span>
                                    {data.errors && data.errors.length > 0 && (
                                      <span className="text-red-600">Errors: {data.errors.length}</span>
                                    )}
                                  </div>
                                  {data.errors && data.errors.length > 0 && (
                                    <div className="mt-1 max-h-24 overflow-y-auto bg-red-50 rounded p-2">
                                      {data.errors.slice(0, 5).map((error, idx) => (
                                        <p key={idx} className="text-xs text-red-600">• {error}</p>
                                      ))}
                                      {data.errors.length > 5 && (
                                        <p className="text-xs text-gray-500 mt-1">... and {data.errors.length - 5} more errors</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-lg sticky bottom-0">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-500">
              {activeTab === 'export' ? 
                '💡 Use "Full Backup" for complete data backup. Images are stored separately in the uploads folder.' : 
                '💡 Only JSON backup files created from this system can be restored'}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              disabled={isLoading}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportImportModal;